import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, AlertCircle, RefreshCw } from "lucide-react";
import { Client } from "@/types";
import { ClientService } from "@/services/ClientService";
import { ClientList } from "./ClientList";
import { ClientForm } from "./ClientForm";
import { ClientDetails } from "./ClientDetails";
import { useAuth } from "@/context/AuthContext";
import { useRepairShopContext } from "@/context/RepairShopContext";

export const ClientManagement: React.FC = () => {
  const { user } = useAuth();
  const { repairShop } = useRepairShopContext();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Dialog states
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Selected client for operations
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  useEffect(() => {
    if (repairShop?.id) {
      loadClients();
    }
  }, [repairShop?.id]);

  useEffect(() => {
    // Filter clients based on search term
    if (searchTerm.trim() === "") {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(
        (client) =>
          client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  }, [clients, searchTerm]);

  const loadClients = async () => {
    if (!repairShop?.id) return;

    try {
      setLoading(true);
      setError(null);
      const clientsData = await ClientService.getClients(repairShop.id);
      setClients(clientsData);
    } catch (err) {
      console.error("Error loading clients:", err);
      setError(err instanceof Error ? err.message : "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = async (term: string) => {
    setSearchTerm(term);

    // If search term is provided and we have a repair shop, perform server-side search
    if (term.trim() && repairShop?.id) {
      try {
        const searchResults = await ClientService.searchClients(
          repairShop.id,
          term
        );
        setFilteredClients(searchResults);
      } catch (err) {
        console.error("Error searching clients:", err);
        // Fall back to client-side filtering
        const filtered = clients.filter(
          (client) =>
            client.name.toLowerCase().includes(term.toLowerCase()) ||
            client.phone?.toLowerCase().includes(term.toLowerCase()) ||
            client.email?.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredClients(filtered);
      }
    }
  };

  const handleAddClient = () => {
    setSelectedClient(null);
    setShowForm(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setShowForm(true);
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setShowDetails(true);
  };

  const handleDeleteClient = (client: Client) => {
    setSelectedClient(client);
    setShowDeleteDialog(true);
  };

  const handleSaveClient = (savedClient: Client) => {
    if (selectedClient) {
      // Update existing client
      setClients((prev) =>
        prev.map((client) =>
          client.id === savedClient.id ? savedClient : client
        )
      );
    } else {
      // Add new client
      setClients((prev) => [savedClient, ...prev]);
    }
    setShowForm(false);
    setSelectedClient(null);
  };

  const confirmDeleteClient = async () => {
    if (!selectedClient) return;

    try {
      await ClientService.deleteClient(selectedClient.id);
      setClients((prev) =>
        prev.map((client) =>
          client.id === selectedClient.id
            ? { ...client, isActive: false }
            : client
        )
      );
      setShowDeleteDialog(false);
      setSelectedClient(null);
    } catch (err) {
      console.error("Error deleting client:", err);
      setError(err instanceof Error ? err.message : "Failed to delete client");
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedClient(null);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedClient(null);
  };

  if (!repairShop) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No repair shop context found. Please ensure you're logged in properly.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Client Management</h1>
          <p className="text-gray-600 mt-1">
            Manage your clients and their information
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={loadClients} disabled={loading}>
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button onClick={handleAddClient}>
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Client List */}
      <ClientList
        clients={filteredClients}
        loading={loading}
        onClientEdit={handleEditClient}
        onClientDelete={handleDeleteClient}
        onClientView={handleViewClient}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
      />

      {/* Add/Edit Client Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedClient ? "Edit Client" : "Add New Client"}
            </DialogTitle>
          </DialogHeader>
          <ClientForm
            client={selectedClient || undefined}
            onSave={handleSaveClient}
            onCancel={handleCloseForm}
            repairShopId={repairShop.id}
            createdBy={user?.id || ""}
          />
        </DialogContent>
      </Dialog>

      {/* Client Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Client Details</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <ClientDetails
              client={selectedClient}
              onEdit={handleEditClient}
              onDelete={handleDeleteClient}
              onClose={handleCloseDetails}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedClient?.name}"? This
              will deactivate the client and they won't appear in client
              selection lists. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteClient}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Client
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
